import {
  Tag,
  FileIngestionResults,
  getFileIngestionErrors,
  getDataType,
  isOsfFile,
  sortConfigFilesBySensorOrder,
  useUploadService,
  BulkUploaderWindow,
  MinimizedUploaderWidget,
  OUSTER_SUPPORTED,
} from '@ouster/webviz-react-components';
import type {
  DataSet,
  Drive,
  FileIngestionErrors,
  FileProcessingStatus,
  FileProcessingStatusState,
  SignedUrlResponse,
  UploadDataSet,
  UploadSession,
  UploadStats,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useState, useRef } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { BASE_ORG_API } from '../../utils/constants';
import { createDrive, getFilesByUploadId } from '../../utils/createDrives';
import { fetchWithAuth } from '../../utils/requests';

export default function UploaderContainer() {
  const { curUserOrg, isOusterUser, user } = useStoreState(
    (storeState) => storeState.user
  );
  const { uploadModalStatus } = useStoreState(
    (storeState) => storeState.recordingUpload
  );
  const { dataCategories } = useStoreState((storeState) => ({
    ...storeState.dataCategory,
  }));
  const { tags } = useStoreState((storeState) => ({
    ...storeState.recordingTag,
  }));
  const { orgSpaceUsage } = useStoreState(
    (storeState) => storeState.organization
  );

  const { fetchOrgSpaceUsage } = useStoreActions(
    (actions) => actions.organization
  );
  const { setUploadModalStatus } = useStoreActions(
    (actions) => actions.recordingUpload
  );
  const { loadDataCategories } = useStoreActions(
    (actions) => actions.dataCategory
  );
  const { fetchTags, createTag } = useStoreActions(
    (actions) => actions.recordingTag
  );

  // Lifted state for file management (persists across minimize/restore)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [configFiles, setConfigFiles] = useState<File[]>([]);
  const [dataSets, setDataSets] = useState<DataSet[]>([]);
  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [isRestoringFromMinimized, setIsRestoringFromMinimized] =
    useState(false);

  // Stable refs for curUserOrg and user so upload service callbacks never change
  // identity when the org switches (which would cause useUploadService to re-init
  // and cancel all in-flight uploads)
  const curUserOrgRef = useRef(curUserOrg);
  const userRef = useRef(user);
  useEffect(() => {
    curUserOrgRef.current = curUserOrg;
  }, [curUserOrg]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Maps sessionId -> orgId so callbacks always use the org that created the
  // session, even if the user switches orgs while the upload is in flight
  // so when FAE upload in provisioned organization, they can switch back to their main org without interrupting the upload
  const sessionOrgMapRef = useRef<Map<string, number>>(new Map());

  // Toast hook for offline warnings
  const { showToast } = useChakraHook();
  const offlineWarningShownRef = useRef(false);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadDataCategories(curUserOrg.id);
      fetchTags(curUserOrg.id);
    }
  }, [curUserOrg?.id, fetchTags, loadDataCategories]);

  useEffect(() => {
    if (uploadModalStatus === 'ACTIVE_MAX' && curUserOrg?.id) {
      fetchTags(curUserOrg.id);
    }
  }, [curUserOrg?.id, fetchTags, uploadModalStatus]);

  const supportedFiles = OUSTER_SUPPORTED;

  // create upload session, for each dataset upload, create session first
  const onCreateUploadSession = useCallback(
    async (uploadDataSet: UploadDataSet): Promise<UploadSession> => {
      const orgId = curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const dataType = getDataType(uploadDataSet.dataFile.name);
      const uploadUrl = `${BASE_ORG_API}/${orgId}/uploads/`;
      const response = await fetchWithAuth(uploadUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: dataType }),
      });

      if (!response.ok) {
        throw new Error('Failed to create upload session');
      }

      const responseBody = (await response.json()) as {
        uuid: string;
        source_bucket?: string;
      };

      if (responseBody.source_bucket) {
        localStorage.setItem('source_bucket', responseBody.source_bucket);
      }

      // Record which org owns this session so later callbacks use the right org
      sessionOrgMapRef.current.set(responseBody.uuid, orgId);

      return {
        session_id: responseBody.uuid,
        status: 'created',
      };
    },
    []
  );

  // generate signed URL for file upload
  const onGenerateSignedUrl = useCallback(
    async (fileName: string, sessionId: string): Promise<SignedUrlResponse> => {
      const orgId =
        sessionOrgMapRef.current.get(sessionId) ?? curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }

      // Get signed URL for file upload within existing session
      const fileUploadUrl = `${BASE_ORG_API}/${orgId}/uploads/${sessionId}/files/`;
      const signedUrlResponse = await fetchWithAuth(fileUploadUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: fileName }),
      });

      if (!signedUrlResponse.ok) {
        throw new Error('Failed to get signed URL');
      }

      const signedUrlBody = (await signedUrlResponse.json()) as {
        signed_url: string;
        uuid: string;
      };
      return {
        signed_url: signedUrlBody.signed_url,
        upload_id: signedUrlBody.uuid,
        session_id: sessionId,
      };
    },
    []
  );

  // Return XMLHttpRequest immediately to allow upload cancellation
  // then return a Promise that resolves/rejects based on upload outcome
  const onUploadFileToSignedUrl = useCallback(
    (
      file: File,
      signedUrl: string,
      onProgress: (progress: number) => void
    ): { xhr: XMLHttpRequest; promise: Promise<void> } => {
      const xhr = new XMLHttpRequest();

      const promise = new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (evt) => {
          const progress = Math.min((evt.loaded / evt.total) * 100, 100);
          onProgress(progress);
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed due to network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.send(file);
      });

      return { xhr, promise };
    },
    []
  );

  // poll file upload status
  const onPollFileUploadStatus = useCallback(
    async (
      sessionId: string,
      fileUploadId: string,
      fileType?: 'data' | 'config'
    ): Promise<FileProcessingStatus> => {
      const orgId =
        sessionOrgMapRef.current.get(sessionId) ?? curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }

      const statusUrl = `${BASE_ORG_API}/${orgId}/uploads/${sessionId}/files/${fileUploadId}`;
      const response = await fetchWithAuth(statusUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to get upload status');
      }

      const statusBody = (await response.json()) as {
        state?: FileProcessingStatusState;
        ingestion_result?: FileIngestionResults;
        errors?: FileIngestionErrors;
      };

      // Process errors the same way as UploadView.tsx
      // getFileIngestionErrors returns [warnings, fatalErrors]
      const [ingestionWarnings, fatalErrors] = statusBody.errors
        ? getFileIngestionErrors(statusBody.errors)
        : [[], []];

      // Keep warnings and errors separate (don't combine them)
      const warnings = ingestionWarnings.map((warning) => warning.message);
      const errors = fatalErrors.map((error) => error.message);

      return {
        file_id: fileUploadId,
        session_id: sessionId,
        state: statusBody.state || 'waiting',
        ingestion_result: statusBody.ingestion_result,
        warnings: warnings.length > 0 ? warnings : undefined,
        errors: errors.length > 0 ? errors : undefined,
        file_type: fileType, // Now we can track data vs config files
      };
    },
    []
  );

  // Poll session until validation completes
  const pollSessionValidation = async (
    sessionId: string,
    orgId: string
  ): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const validationPolling = setInterval(() => {
        fetchWithAuth(`${BASE_ORG_API}/${orgId}/uploads/${sessionId}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
          .then((validationResponse) => validationResponse.json())
          .then((validationData: { state?: string }) => {
            if (
              validationData.state === 'moving' ||
              validationData.state === 'closed'
            ) {
              // Validation successful, drive is ready
              clearInterval(validationPolling);
              resolve();
            } else if (
              validationData.state === 'failed' ||
              validationData.state === 'validation_failed'
            ) {
              // Validation failed
              clearInterval(validationPolling);
              reject(
                new Error('Dataset validation failed during final processing')
              );
            }
            // Continue polling if state is still processing
          })
          .catch((error) => {
            clearInterval(validationPolling);
            reject(error);
          });
      }, 1000); // Poll every second like UploadView.tsx
    });

  // Clean up drive on error by deleting it and removing localStorage entry
  const cleanupDriveOnError = async (
    driveId: string,
    orgId: string
  ): Promise<void> => {
    try {
      const deleteUrl = `${BASE_ORG_API}/${orgId}/recordings/${driveId}`;
      await fetchWithAuth(deleteUrl, { method: 'DELETE' });
      localStorage.removeItem('drive_id');
    } catch (cleanupError) {
      console.error('Failed to clean up drive after error:', cleanupError);
    }
  };

  const onCreateRecordingFromSession = useCallback(
    async (uploadDataSet: UploadDataSet): Promise<Drive> => {
      const orgId = uploadDataSet.uploadSessionId
        ? sessionOrgMapRef.current.get(uploadDataSet.uploadSessionId) ??
          curUserOrgRef.current?.id
        : curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }

      try {
        // Step 1: Prepare config file results
        let configInfo = uploadDataSet.ingestionResults;

        // For OSF uploads, get all config files from the session (multi-sensor case)
        // Check if it's an OSF upload by looking at the data file extension
        const isOsfUpload = isOsfFile(uploadDataSet.dataFile.name);
        if (isOsfUpload && uploadDataSet.uploadSessionId) {
          configInfo = await getFilesByUploadId(
            uploadDataSet.uploadSessionId,
            orgId
          );
        }
        // Step 2: Sort sensor info by product serial number using utility
        if (configInfo && configInfo.length > 0) {
          configInfo = sortConfigFilesBySensorOrder(configInfo);
        }

        // Step 3: Create drive using sorted config info
        const driveResponse = await createDrive(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          uploadDataSet.tags || [],
          orgId,
          uploadDataSet.uploadSessionId,
          uploadDataSet.dataCategory,
          configInfo
        );

        if (!driveResponse?.ok) {
          throw new Error(
            `Failed to create drive for session ${uploadDataSet.uploadSessionId}`
          );
        }

        const driveData = (await driveResponse.json()) as { drive_id: string };

        // Store drive_id for cleanup on validation error
        if (driveData.drive_id) {
          localStorage.setItem('drive_id', driveData.drive_id.toString());
        }

        // Step 4: Close the upload session with retry logic
        const closeSessionUrl = `${BASE_ORG_API}/${orgId}/uploads/${uploadDataSet.uploadSessionId}`;
        const refreshToken = localStorage.getItem('kcRefreshToken');

        // Define our retry configuration
        const retryDelays = [1000, 3000, 5000]; // 1s, 3s, 5s
        let lastError: Error | null = null;
        let closeResponse: Response | null = null;

        for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
          try {
            // eslint-disable-next-line no-await-in-loop
            closeResponse = await fetchWithAuth(closeSessionUrl, {
              method: 'PATCH',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                state: 'closed',
                refresh_token: refreshToken,
                attributes: {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                  tags: (uploadDataSet.tags || []).map((tag) => tag.id),
                  data_category: uploadDataSet.dataCategory?.id,
                  user: userRef.current?.name || 'John Doe',
                },
              }),
            });

            // If successful, break the loop
            if (closeResponse.ok) break;

            // If we get here, it's a 4xx or 5xx error
            throw new Error(
              `Server returned ${closeResponse.status}: ${closeResponse.statusText}`
            );
          } catch (err) {
            lastError = err as Error;

            // If we have exhausted our retries, don't wait, just let it fail
            if (attempt === retryDelays.length) break;

            // Log retry attempt for debugging
            console.warn(
              `Attempt ${attempt + 1} to close session failed. Retrying in ${
                retryDelays[attempt]
              }ms...`
            );

            // Wait for the specified duration before next iteration
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
              setTimeout(resolve, retryDelays[attempt]);
            });
          }
        }

        // Final check after loop: If we never got a successful response, throw the last error
        if (!closeResponse || !closeResponse.ok) {
          throw new Error(
            `Failed to close upload session ${
              uploadDataSet.uploadSessionId
            } after ${retryDelays.length + 1} attempts. Last error: ${
              lastError?.message
            }`
          );
        }

        // Step 5: Poll session until validation completes using utility
        try {
          if (!uploadDataSet.uploadSessionId) {
            throw new Error('No session ID available for validation');
          }
          await pollSessionValidation(
            uploadDataSet.uploadSessionId,
            orgId.toString()
          );
          localStorage.removeItem('drive_id'); // Clean up on success
          // Session complete — remove from map
          if (uploadDataSet.uploadSessionId) {
            sessionOrgMapRef.current.delete(uploadDataSet.uploadSessionId);
          }

          // Dispatch event with drive metadata for optimistic UI insertion
          window.dispatchEvent(
            new CustomEvent('upload-drive-created', {
              detail: {
                driveId: Number(driveData.drive_id),
                orgId,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                tags: (uploadDataSet.tags || []).map((tag) => tag.name),
                dataCategory: uploadDataSet.dataCategory?.name ?? null,
                dataFile: uploadDataSet.dataFile.name,
                uploadedBy: userRef.current?.name ?? null,
                processedAt: new Date()
                  .toISOString()
                  .replace('Z', '')
                  .replace(/(\.\d{3})$/, '$1000'),
                sensorInfo: (configInfo || []).map(
                  (s: FileIngestionResults) => ({
                    sdk_prod_sn: s.prod_sn ?? '',
                    sdk_prod_line: s.prod_line ?? '',
                    sdk_prod_type: s.prod_type ?? '',
                    sdk_prod_generation: s.prod_generation ?? '',
                    sdk_prod_hw_rev: s.prod_hw_rev ?? '',
                    sdk_prod_beam_spacing: s.prod_beam_spacing ?? '',
                    sdk_prod_resolution: s.prod_resolution ?? '',
                    sdk_fw_version_core: s.fw_version_core ?? '',
                    sdk_lidar_mode: s.lidar_mode ?? '',
                    sdk_fw_branch: s.fw_branch ?? '',
                    sdk_fw_build_rev: s.build_rev ?? '',
                    sdk_fw_image_rev: s.image_rev ?? '',
                  })
                ),
              },
            })
          );

          return {
            drive_id: driveData.drive_id,
            status: 'created',
          };
        } catch (error) {
          // Clean up drive on validation failure
          if (driveData.drive_id) {
            await cleanupDriveOnError(driveData.drive_id, orgId.toString());
          }
          throw error;
        }
      } catch (error) {
        // Clean up drive on any error
        const driveId = localStorage.getItem('drive_id');
        if (driveId) {
          await cleanupDriveOnError(driveId, orgId.toString());
        }
        // Remove session from map on error too
        if (uploadDataSet.uploadSessionId) {
          sessionOrgMapRef.current.delete(uploadDataSet.uploadSessionId);
        }

        throw error instanceof Error
          ? error
          : new Error('Failed to create recording from session');
      }
    },
    []
  );

  // Additional callback handlers for upload events
  const onUploadSuccess = useCallback(() => {
    const orgId = curUserOrgRef.current?.id;
    if (orgId) {
      fetchOrgSpaceUsage(orgId);
    }
  }, [fetchOrgSpaceUsage]);

  const onUploadError = useCallback(
    (
      error: Error,
      context: { datasetId: string; sessionId?: string; fileId?: string }
    ) => {
      console.error('Upload error:', error, context);
      // Could show error notification, log to monitoring service, etc.
      // Context provides datasetId and optionally sessionId/fileId for debugging
    },
    []
  );

  // Add cleanup callback to handle file deletion
  const onCleanupUploadFiles = useCallback(
    (
      uploadSessionId: string,
      dataUploadId?: string,
      configUploadIds?: string[]
    ) => {
      const orgId =
        sessionOrgMapRef.current.get(uploadSessionId) ??
        curUserOrgRef.current?.id;
      if (!orgId) return;

      // Delete data file if it exists
      if (dataUploadId) {
        const dataDeleteURL = `${BASE_ORG_API}/${orgId}/uploads/${uploadSessionId}/files/${dataUploadId}`;
        fetchWithAuth(dataDeleteURL, {
          method: 'DELETE',
        }).catch((error) => {
          console.warn(`Failed to delete data file ${dataUploadId}:`, error);
        });
      }

      // Delete config files if they exist
      if (configUploadIds && Array.isArray(configUploadIds)) {
        configUploadIds.forEach((id) => {
          const configDeleteURL = `${BASE_ORG_API}/${orgId}/uploads/${uploadSessionId}/files/${id}`;
          fetchWithAuth(configDeleteURL, {
            method: 'DELETE',
          }).catch((error) => {
            console.warn(`Failed to delete config file ${id}:`, error);
          });
        });
      }
    },
    []
  );

  const onCreateTag = useCallback(
    async (name: string) => {
      if (!curUserOrg?.id)
        return Promise.reject(new Error('No organization found'));
      try {
        const newTag = (await createTag({
          organization_id: curUserOrg.id,
          name,
        })) as Tag;
        return newTag;
      } catch (error) {
        console.error('Error creating tag:', error);
        return Promise.reject(error);
      }
    },
    [createTag, curUserOrg?.id]
  );

  const uploadService = useUploadService({
    onCreateUploadSession,
    onGenerateSignedUrl,
    onUploadFileToSignedUrl,
    onPollFileUploadStatus,
    onCreateRecordingFromSession,
    onUploadSuccess,
    onUploadError,
    onCleanupUploadFiles,
    maxConcurrentUploads: 3,
  });

  // implement wakeLock here to prevent system sleep during active upload
  const { uploadingCount, processingCount, validatingCount } =
    uploadService.uploadStats as UploadStats;

  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);

  const activeCount = uploadingCount + processingCount + validatingCount;

  // Request wake lock when uploads are active
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator && !wakeLock) {
      try {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);

        // Handle release events
        lock.addEventListener('release', () => {
          setWakeLock(null);
        });
      } catch (error) {
        // Wake lock request may fail if page is not visible or focused
        // This is expected behavior and not an error we need to log
        // The uploads will continue normally without preventing screen sleep
      }
    }
  }, [wakeLock]);

  // Release wake lock
  const releaseWakeLock = useCallback(() => {
    if (wakeLock) {
      wakeLock.release();
      setWakeLock(null);
    }
  }, [wakeLock]);

  // Clear uploader state when organization changes
  useEffect(() => {
    const exitFromProvisioning =
      sessionStorage.getItem('exitFromProvisioning') === 'true';
    const uploadsActive = sessionStorage.getItem('uploadsActive');

    if (exitFromProvisioning) {
      if (uploadsActive) {
        // Defer showToast past the current render/navigation flush so the
        // Chakra toast scheduler isn't preempted by the org-change re-render
        setTimeout(() => {
          showToast({
            title: 'Exit from provisioning mode',
            description: `Your upload will continue in the background.`,
            status: 'info',
          });
        }, 0);
      }

      sessionStorage.removeItem('exitFromProvisioning');
      return; // Don't clear state on initial load when exiting provisioning manager
    }

    setSelectedFiles([]);
    setConfigFiles([]);
    setDataSets([]);
    uploadService.clearAllUploads();
  }, [curUserOrg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage wake lock based on active upload count and page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeCount > 0) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check
    if (activeCount > 0) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeCount, requestWakeLock, releaseWakeLock]); // Release wake lock when component unmounts
  useEffect(() => () => releaseWakeLock(), [releaseWakeLock]);

  // Persist whether uploads are active so other UI (e.g. org switcher) can check
  useEffect(() => {
    try {
      if (activeCount > 0) {
        sessionStorage.setItem('uploadsActive', 'true');
      } else {
        sessionStorage.removeItem('uploadsActive');
      }
    } catch (e) {
      // sessionStorage may throw in some environments; ignore safely
    }
  }, [activeCount]);

  // Monitor offline status during active uploads
  useEffect(() => {
    const handleOnline = () => {
      offlineWarningShownRef.current = false;
    };

    const handleOffline = () => {
      try {
        const uploadsActive =
          sessionStorage.getItem('uploadsActive') === 'true';
        if (uploadsActive && !offlineWarningShownRef.current) {
          offlineWarningShownRef.current = true;
          showToast({
            title: 'Connection Lost',
            description:
              'Your device went offline. Active uploads will be interrupted. Please check your internet connection.',
            status: 'warning',
            isStay: true,
          });
        }
      } catch (error) {
        console.error('Error handling offline event:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  // Listen for a cross-window/component cancel event so the org switcher can request cancellations
  useEffect(() => {
    const handleCancelAll = () => {
      try {
        uploadService.cancelAllUploads();
        // update persisted flag
        sessionStorage.removeItem('uploadsActive');
      } catch (error) {
        console.error('Failed to cancel uploads from external request', error);
      }
    };

    window.addEventListener(
      'cancelAllUploads',
      handleCancelAll as EventListener
    );
    return () => {
      window.removeEventListener(
        'cancelAllUploads',
        handleCancelAll as EventListener
      );
    };
  }, [uploadService]);

  const closeUploaderHandler = () => {
    // clean state from fileManagement
    setSelectedFiles([]);
    setConfigFiles([]);
    setDataSets([]);

    // clean state from uploaderService
    uploadService.clearAllUploads();

    // Only clear uploads if no active uploads
    setUploadModalStatus('INACTIVE');
  };

  const restoreUploaderHandler = () => {
    const uploadStats = uploadService.uploadStats as UploadStats;
    if (uploadStats.totalCount !== 0) {
      setIsRestoringFromMinimized(true);
    }
    setUploadModalStatus('ACTIVE_MAX');
  };

  const minimizeUploaderHandler = () => {
    setUploadModalStatus('ACTIVE_MIN');
  };

  // Reset restoring state when modal becomes active
  useEffect(() => {
    if (uploadModalStatus === 'ACTIVE_MAX') {
      setIsRestoringFromMinimized(false);
    }
  }, [uploadModalStatus]);

  if (!curUserOrg) {
    return null;
  }

  return (
    <>
      {uploadModalStatus === 'ACTIVE_MAX' && (
        <BulkUploaderWindow
          orgName={curUserOrg.name}
          orgSpaceUsage={orgSpaceUsage}
          supportedFiles={supportedFiles}
          dataCategories={dataCategories ?? []}
          tags={tags}
          isOusterUser={isOusterUser}
          activeCount={activeCount}
          onCreateTag={onCreateTag}
          onMinimize={minimizeUploaderHandler}
          onClose={closeUploaderHandler}
          defaultActiveTab={isRestoringFromMinimized ? 1 : 0}
          // Pass the upload service instance
          uploadService={uploadService}
          // Pass lifted state
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          configFiles={configFiles}
          setConfigFiles={setConfigFiles}
          dataSets={dataSets}
          setDataSets={setDataSets}
          isEnqueuing={isEnqueuing}
          setIsEnqueuing={setIsEnqueuing}
        />
      )}
      {uploadModalStatus === 'ACTIVE_MIN' && (
        <MinimizedUploaderWidget
          onRestore={restoreUploaderHandler}
          uploadStats={uploadService.uploadStats as UploadStats}
        />
      )}
    </>
  );
}
